/**
 * CreateFolderDialog
 *
 * Modal that asks for a new folder name. The folder is created in the
 * current folder context (or the root). Submission happens on Enter or
 * by clicking "Create Folder"; submit is gated on a non-empty name.
 */

import { type FileItem } from "@crm-src/lib/files-api"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material"

interface CreateFolderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentFolder: FileItem | null
  folderName: string
  onFolderNameChange: (value: string) => void
  onCreate: () => void
}

export function CreateFolderDialog({
  open,
  onOpenChange,
  currentFolder,
  folderName,
  onFolderNameChange,
  onCreate,
}: CreateFolderDialogProps) {
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Folder</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Create a new folder in {currentFolder ? `"${currentFolder.name}"` : 'the root directory'}.
        </DialogContentText>

        <Box>
          <TextField
            id="folder-name"
            label="Folder Name"
            fullWidth
            size="small"
            value={folderName}
            onChange={(e) => onFolderNameChange(e.target.value)}
            placeholder="Enter folder name..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && folderName.trim()) {
                e.preventDefault()
                onCreate()
              }
            }}
            autoFocus
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onCreate} disabled={!folderName.trim()}>
          Create Folder
        </Button>
      </DialogActions>
    </Dialog>
  )
}
